import React from 'react';

export interface GoogleLogoIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 24). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Google 'G' mark on a white rounded tile, tinted with the Bombiix palette rather
 * than Google's brand colours. Used for in-product decoration (the SSO settings
 * rail and connected-directory chip) — never for a Google-branded sign-in button,
 * which must carry the official multicolour logo (see GoogleLogo). The four
 * layered fills from the design export are collapsed to the single rendered fill
 * per segment (the later paint wins), keeping the visual identical.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the square Google mark SVG element.
 */
function GoogleLogoIcon({ size = 24, className = '', title, ...rest }: GoogleLogoIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect width="20" height="20" rx="4" fill="var(--color-white)" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.43 9.93129C17.43 9.36401 17.3791 8.81856 17.2845 8.29492H9.75V11.3895H14.0555C13.87 12.3895 13.3064 13.2367 12.4591 13.804V15.8113H15.0445C16.5573 14.4186 17.43 12.3676 17.43 9.93129Z"
        fill="#6DC1F5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.74968 17.7495C11.9097 17.7495 13.7206 17.0332 15.0442 15.8113L12.4588 13.8041C11.7424 14.2841 10.826 14.5677 9.74968 14.5677C7.66604 14.5677 5.9024 13.1604 5.27331 11.2695H2.60059V13.3423C3.91695 15.9568 6.6224 17.7495 9.74968 17.7495Z"
        fill="#73C5AF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.27364 11.2709C5.11364 10.7909 5.02273 10.2782 5.02273 9.75093C5.02273 9.22366 5.11364 8.71093 5.27364 8.23093V6.1582H2.60091C2.05909 7.2382 1.75 8.46002 1.75 9.75093C1.75 11.0418 2.05909 12.2637 2.60091 13.3437L5.27364 11.2709Z"
        fill="#FDD499"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.74968 4.93182C10.9242 4.93182 11.9788 5.33545 12.8079 6.12818L15.1024 3.83364C13.7169 2.54273 11.906 1.75 9.74968 1.75C6.6224 1.75 3.91695 3.54273 2.60059 6.15727L5.27331 8.23C5.9024 6.33909 7.66604 4.93182 9.74968 4.93182Z"
        fill="#FA979B"
      />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(GoogleLogoIcon);
